import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAdminData from '../hooks/useAdminData'
import SubmissionsTable from '../components/admin/SubmissionsTable'
import ConfirmDialog from '../components/admin/ConfirmDialog'
import { ErrorAlert } from '../components/ui'
import { exportSubmissionsToCSV } from '../lib/csvExport'
import { db } from '../lib/supabase'

export default function AdminDashboard() {
  const { t } = useTranslation('admin')
  const { submissions, loading, error, refresh, removeSubmission } = useAdminData()
  const navigate = useNavigate()

  const [sortConfig, setSortConfig] = useState({ key: 'completed_at', direction: 'desc' })
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [sexFilter, setSexFilter] = useState('all')
  const [facilitatorFilter, setFacilitatorFilter] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Enrich submissions with computed couple_name for the Couple column
  const enrichedSubmissions = useMemo(() => {
    return submissions.map(s => {
      let couple_name = null
      if (s.partner_name) {
        if (s.biological_sex === 'Female') {
          couple_name = `${s.partner_name} / ${s.name}`
        } else {
          couple_name = `${s.name} / ${s.partner_name}`
        }
      }
      return { ...s, couple_name }
    })
  }, [submissions])

  // Unique facilitator names for the dropdown
  const facilitatorNames = useMemo(() => {
    const names = new Set()
    enrichedSubmissions.forEach(s => {
      if (s.facilitator_name) names.add(s.facilitator_name)
    })
    return Array.from(names).sort()
  }, [enrichedSubmissions])

  // Apply filters
  const filteredSubmissions = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    return enrichedSubmissions.filter(s => {
      // Search filter
      if (query) {
        const fields = [s.name, s.email, s.partner_name, s.facilitator_name]
        const matches = fields.some(f => f && f.toLowerCase().includes(query))
        if (!matches) return false
      }
      if (sexFilter !== 'all' && s.biological_sex !== sexFilter) return false
      if (facilitatorFilter !== 'all') {
        if (facilitatorFilter === 'none') {
          if (s.facilitator_name != null) return false
        } else {
          if (s.facilitator_name !== facilitatorFilter) return false
        }
      }
      if (dateFrom) {
        const submitted = new Date(s.completed_at).toLocaleDateString('sv-SE')
        if (submitted < dateFrom) return false
      }
      if (dateTo) {
        const submitted = new Date(s.completed_at).toLocaleDateString('sv-SE')
        if (submitted > dateTo) return false
      }
      return true
    })
  }, [enrichedSubmissions, searchQuery, sexFilter, facilitatorFilter, dateFrom, dateTo])

  // Sort filtered results
  const sortedSubmissions = useMemo(() => {
    const sorted = [...filteredSubmissions]
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key]
      let bVal = b[sortConfig.key]

      // Handle nulls
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      // Strings
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = (bVal || '').toLowerCase()
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [filteredSubmissions, sortConfig])

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleRowClick = (id) => {
    navigate(`/admin/submission/${id}`)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleteError(null)
    try {
      await db.deleteSubmission(deleteTarget.id)
      removeSubmission(deleteTarget.id)
      setDeleteTarget(null)
    } catch (err) {
      setDeleteError(err.message)
    }
  }

  const handleCoupleAnalysis = (id) => {
    navigate(`/admin/couple-analysis/${id}`)
  }

  const handleExport = () => {
    exportSubmissionsToCSV(sortedSubmissions)
  }

  const hasActiveFilters = searchQuery || sexFilter !== 'all' || facilitatorFilter !== 'all' || dateFrom || dateTo

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-400 text-lg">{t('adminDashboard.loadingSubmissions')}</div>
      </div>
    )
  }

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-medium text-gray-800">{t('adminDashboard.title')}</h2>
          <p className="text-gray-500 text-sm mt-1">
            {hasActiveFilters
              ? t('adminDashboard.filteredCount', { filtered: filteredSubmissions.length, total: submissions.length })
              : t('adminDashboard.submissionCount', { count: submissions.length })
            }
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={refresh}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg transition-all hover:bg-gray-50"
          >
            {t('adminDashboard.refresh')}
          </button>
          <button
            onClick={handleExport}
            disabled={submissions.length === 0}
            className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('adminDashboard.exportCsv')}
          </button>
        </div>
      </div>

      <ErrorAlert message={error} />
      <ErrorAlert message={deleteError} onDismiss={() => setDeleteError(null)} />

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-4 mb-4 p-4 bg-white rounded-xl shadow-sm">
        <div className="flex flex-col flex-1 min-w-[200px]">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t('adminDashboard.filters.search')}
          </label>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('adminDashboard.filters.searchPlaceholder')}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t('adminDashboard.filters.sex')}
          </label>
          <select
            value={sexFilter}
            onChange={(e) => setSexFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="all">{t('adminDashboard.filters.all')}</option>
            <option value="Male">{t('adminDashboard.filters.male')}</option>
            <option value="Female">{t('adminDashboard.filters.female')}</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t('adminDashboard.filters.facilitator')}
          </label>
          <select
            value={facilitatorFilter}
            onChange={(e) => setFacilitatorFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="all">{t('adminDashboard.filters.all')}</option>
            <option value="none">{t('adminDashboard.filters.noFacilitator')}</option>
            {facilitatorNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t('adminDashboard.filters.from')}
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t('adminDashboard.filters.to')}
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setSearchQuery('')
              setSexFilter('all')
              setFacilitatorFilter('all')
              setDateFrom('')
              setDateTo('')
            }}
            className="px-3 py-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
          >
            {t('adminDashboard.filters.clearFilters')}
          </button>
        )}
      </div>

      {/* Table */}
      <SubmissionsTable
        submissions={sortedSubmissions}
        sortConfig={sortConfig}
        onSort={handleSort}
        onRowClick={handleRowClick}
        onDelete={setDeleteTarget}
        onCoupleAnalysis={handleCoupleAnalysis}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('adminDashboard.deleteSubmission.title')}
        message={t('adminDashboard.deleteSubmission.message', { name: deleteTarget?.name })}
        confirmText={t('adminDashboard.deleteSubmission.confirmText')}
        danger
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
